import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import QuickDateFilters, { ViewMode } from '../components/KidsClub/QuickDateFilters';
import ParentDayDetailDialog from '../components/swim/ParentDayDetailDialog';
import EmptyState from '../components/common/EmptyState';
import { useSnackbar } from '../components/common/SnackbarProvider';
import swimService from '../api/services/swimService';
import { MyCalendarEntry, SwimCompetitionRow, SwimEventRow } from '../types/swim';

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function to12Hour(hhmm: string | null): string {
  if (!hhmm) return '';
  const [hStr, m] = hhmm.split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m ?? '00'} ${period}`;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfWeekSunday(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay());
  return out;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  out.setHours(23, 59, 59, 999);
  return out;
}

function computeRange(anchor: Date, view: ViewMode): { start: Date; end: Date } {
  if (view === 'day') {
    const start = new Date(anchor); start.setHours(0, 0, 0, 0);
    const end = new Date(anchor); end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (view === 'week') {
    const start = startOfWeekSunday(anchor);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return { start, end };
  }
  const monthStart = startOfMonth(anchor);
  const monthEnd = endOfMonth(anchor);
  const start = startOfWeekSunday(monthStart);
  const end = new Date(monthEnd);
  end.setDate(end.getDate() + (6 - end.getDay()));
  return { start, end };
}

function rangeLabel(anchor: Date, view: ViewMode): string {
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const fullFmt = (d: Date) => d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const monthFmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  if (view === 'day') return fullFmt(anchor);
  if (view === 'week') {
    const start = startOfWeekSunday(anchor);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return `${fmt(start)} – ${fmt(end)}, ${start.getFullYear()}`;
  }
  return monthFmt(anchor);
}

function weekKey(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  return monday.toISOString().slice(0, 10);
}
function weekLabel(mondayIso: string): string {
  const m = new Date(mondayIso + 'T00:00:00');
  const sunday = new Date(m); sunday.setDate(m.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `Week of ${fmt(m)} – ${fmt(sunday)}`;
}
function dayLabel(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

const MyScheduleParentPage: React.FC = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();

  const [tabValue, setTabValue] = useState<number>(0);
  const [view, setView] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [entries, setEntries] = useState<MyCalendarEntry[]>([]);
  const [competitions, setCompetitions] = useState<SwimCompetitionRow[]>([]);
  const [events, setEvents] = useState<SwimEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [openDate, setOpenDate] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(toIsoDate(new Date()));
  const [agendaFilter, setAgendaFilter] = useState<'all' | 'practice' | 'meets'>('all');

  useEffect(() => { void load(); }, []);

  async function load() {
    // Only show the full-screen spinner on the initial load. Subsequent
    // refreshes (after the day-detail dialog saves something) silently
    // swap entries in the background — otherwise the grid/agenda flashes
    // every time the parent toggles an RSVP or meet attendance.
    const initial = entries.length === 0 && competitions.length === 0;
    if (initial) setLoading(true);
    setError(null);
    try {
      const { entries: e, competitions: comps, events: evs } = await swimService.myCalendar();
      setEntries(e);
      setCompetitions(comps);
      setEvents(evs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load schedule');
    } finally {
      if (initial) setLoading(false);
    }
  }

  function navigate(direction: 'prev' | 'next') {
    const next = new Date(anchor);
    if (view === 'day') next.setDate(next.getDate() + (direction === 'next' ? 1 : -1));
    else if (view === 'week') next.setDate(next.getDate() + (direction === 'next' ? 7 : -7));
    else next.setMonth(next.getMonth() + (direction === 'next' ? 1 : -1));
    setAnchor(next);
  }

  // "Today" (day view) jumps to today; week/month keep the current anchor.
  function handleViewChange(v: ViewMode) {
    if (v === 'day') setAnchor(new Date());
    setView(v);
  }

  async function toggleRSVP(entry: MyCalendarEntry) {
    const next = entry.rsvp_status === 'skip' ? 'planned' : 'skip';
    const key = `${entry.enrollment_id}|${entry.session_id}`;
    setBusyKey(key);
    try {
      await swimService.setRSVP(entry.enrollment_id, entry.session_id, next);
      setEntries((prev) =>
        prev.map((e) =>
          e.enrollment_id === entry.enrollment_id && e.session_id === entry.session_id
            ? { ...e, rsvp_status: next }
            : e,
        ),
      );
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update RSVP');
    } finally {
      setBusyKey(null);
    }
  }

  const { start, end } = useMemo(() => computeRange(anchor, view), [anchor, view]);
  const showGrid = view === 'month' || view === 'week';
  const today = useMemo(() => toIsoDate(new Date()), []);
  const currentMonth = anchor.getMonth();
  const DOT = { practice: theme.palette.primary.main, meet: theme.palette.warning.main, social: theme.palette.info.main };

  // Map of date → entries (for calendar grid quick lookup)
  const entriesByDate = useMemo(() => {
    const map = new Map<string, MyCalendarEntry[]>();
    for (const e of entries) {
      if (!e.session_date) continue;
      const list = map.get(e.session_date) ?? [];
      list.push(e);
      map.set(e.session_date, list);
    }
    return map;
  }, [entries]);

  // Map of date → competitions (top-level field from my_calendar). Sourced
  // independently so meets on dates without a practice (Saturday-only
  // invitationals etc.) still surface.
  const competitionsByDate = useMemo(() => {
    const map = new Map<string, SwimCompetitionRow[]>();
    for (const c of competitions) {
      if (!c.competition_date) continue;
      const list = map.get(c.competition_date) ?? [];
      list.push(c);
      map.set(c.competition_date, list);
    }
    return map;
  }, [competitions]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, SwimEventRow[]>();
    for (const ev of events) {
      if (!ev.event_date) continue;
      const list = map.get(ev.event_date) ?? [];
      list.push(ev);
      map.set(ev.event_date, list);
    }
    return map;
  }, [events]);

  // Build the array of cells covering [start, end]
  const cells = useMemo(() => {
    const out: string[] = [];
    const cur = new Date(start);
    while (cur <= end) {
      out.push(toIsoDate(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [start, end]);

  // Pick a sensible selected day when the range/view changes: the viewed day
  // (day view), else today if it's in range, else the first day of the range.
  useEffect(() => {
    if (view === 'day') {
      setSelectedDate(toIsoDate(anchor));
      return;
    }
    setSelectedDate(cells.includes(today) ? today : cells[0] ?? today);
  }, [cells, view, anchor, today]);

  // Agenda grouping — union of practice dates AND competition-only dates.
  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, MyCalendarEntry[]>>();
    const seedDay = (dateIso: string) => {
      const wk = weekKey(dateIso);
      const wkMap = map.get(wk) ?? new Map<string, MyCalendarEntry[]>();
      if (!wkMap.has(dateIso)) wkMap.set(dateIso, []);
      map.set(wk, wkMap);
      return wkMap;
    };
    for (const e of entries) {
      if (!e.session_date) continue;
      const wkMap = seedDay(e.session_date);
      const dayList = wkMap.get(e.session_date) ?? [];
      dayList.push(e);
      wkMap.set(e.session_date, dayList);
    }
    for (const c of competitions) {
      if (!c.competition_date) continue;
      seedDay(c.competition_date);
    }
    for (const ev of events) {
      if (!ev.event_date) continue;
      seedDay(ev.event_date);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([wk, days]) => ({
        weekKey: wk,
        days: Array.from(days.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([day, list]) => ({ day, list })),
      }));
  }, [entries, competitions, events]);

  const upcomingCount = useMemo(
    () => entries.filter((e) => e.session_date && e.session_date >= today && !e.is_cancelled).length,
    [entries, today],
  );

  // Apply the All / Practice / Meets filter to the grouped agenda. Drops days
  // with no visible content and weeks with no visible days so the empty state
  // can render cleanly.
  const filteredGrouped = useMemo(() => {
    return grouped
      .map((wk) => ({
        weekKey: wk.weekKey,
        days: wk.days
          .map(({ day, list }) => {
            const dayComps = competitionsByDate.get(day) ?? [];
            const dayEvents = eventsByDate.get(day) ?? [];
            const hasComp = dayComps.length > 0;
            const hasEvent = dayEvents.length > 0;
            const hasPractice = list.length > 0;
            const showComp = hasComp && agendaFilter !== 'practice';
            const showPractice = hasPractice && agendaFilter !== 'meets';
            const showEvent = hasEvent && agendaFilter === 'all';
            if (!showComp && !showPractice && !showEvent) return null;
            return { day, list, comps: dayComps, events: dayEvents, showComp, showPractice, showEvent };
          })
          .filter(
            (d): d is { day: string; list: MyCalendarEntry[]; comps: SwimCompetitionRow[]; events: SwimEventRow[]; showComp: boolean; showPractice: boolean; showEvent: boolean } =>
              d !== null,
          ),
      }))
      .filter((wk) => wk.days.length > 0);
  }, [grouped, agendaFilter, competitionsByDate, eventsByDate]);

  // Counts for the filter buttons.
  const practiceCount = useMemo(
    () => entries.filter((e) => e.session_date && e.session_date >= today && !e.is_cancelled).length,
    [entries, today],
  );
  // Filter-button count: visible upcoming meets (filtered server-side by
  // program ties, so this naturally excludes meets the parent shouldn't see).
  const meetCount = useMemo(
    () => competitions.filter((c) => c.competition_date && c.competition_date >= today).length,
    [competitions, today],
  );

  // Agenda items for the selected day (practices per swimmer, meets, events).
  type DayAgendaItem = { time: string | null; title: string; sub?: string; kind: 'practice' | 'meet' | 'social' };
  const dayAgenda = useMemo<DayAgendaItem[]>(() => {
    const items: DayAgendaItem[] = [];
    for (const e of entriesByDate.get(selectedDate) ?? []) {
      const status = e.is_cancelled ? 'Cancelled' : e.at_meet ? 'At meet' : e.rsvp_status === 'skip' ? 'Skipping' : 'Planned';
      items.push({
        time: e.start_time,
        title: e.swimmer_name,
        sub: [e.program_name, status].filter(Boolean).join(' · '),
        kind: 'practice',
      });
    }
    for (const c of competitionsByDate.get(selectedDate) ?? []) {
      items.push({ time: c.start_time, title: c.name, sub: c.location ?? undefined, kind: 'meet' });
    }
    for (const ev of eventsByDate.get(selectedDate) ?? []) {
      items.push({ time: ev.start_time, title: ev.name, sub: ev.location ?? undefined, kind: 'social' });
    }
    items.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
    return items;
  }, [selectedDate, entriesByDate, competitionsByDate, eventsByDate]);

  const selDailyWorkout = (entriesByDate.get(selectedDate) ?? [])[0]?.daily_workout ?? null;
  const selHeader = (() => {
    const [y, m, dd] = selectedDate.split('-').map(Number);
    const base = new Date(y, m - 1, dd).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const comps = competitionsByDate.get(selectedDate) ?? [];
    return comps[0] ? `${base} · ${comps[0].name}` : base;
  })();

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              My Swim Schedule
            </Typography>
            <Typography variant="body1" color="text.secondary">
              All upcoming sessions for your swimmers. Default is "Planned" — toggle skip if you can't make it.
            </Typography>
          </Box>
          <IconButton onClick={() => void load()} disabled={loading}><RefreshIcon /></IconButton>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarMonthIcon fontSize="small" />
                  Calendar
                </Box>
              }
            />
            <Tab
              label={
                <Badge badgeContent={upcomingCount} color="primary">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarTodayIcon fontSize="small" />
                    Agenda
                  </Box>
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Tab 0: Calendar grid */}
        {tabValue === 0 && (
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 3 }} spacing={2}>
              <QuickDateFilters
                currentView={view}
                onViewChange={handleViewChange}
                onNavigate={navigate}
                showNavigation={true}
              />
              <Typography variant="h6">{rangeLabel(anchor, view)}</Typography>
            </Stack>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {showGrid && (
                  <Card sx={{ p: { xs: 1, sm: 1.5 }, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                      {DAY_HEADERS.map((h) => (
                        <Typography key={h} variant="caption" align="center" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {h}
                        </Typography>
                      ))}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                      {cells.map((iso) => {
                        const [y, m, dd] = iso.split('-').map(Number);
                        const cellDate = new Date(y, m - 1, dd);
                        const isOtherMonth = view === 'month' && cellDate.getMonth() !== currentMonth;
                        const isToday = iso === today;
                        const isSelected = iso === selectedDate;
                        const hasPractice = (entriesByDate.get(iso) ?? []).length > 0;
                        const hasMeet = (competitionsByDate.get(iso) ?? []).length > 0;
                        const hasSocial = (eventsByDate.get(iso) ?? []).length > 0;
                        return (
                          <Box
                            key={iso}
                            onClick={() => { if (!isOtherMonth) setSelectedDate(iso); }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '4px',
                              py: 0.75,
                              cursor: isOtherMonth ? 'default' : 'pointer',
                              opacity: isOtherMonth ? 0.28 : 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isSelected ? 'primary.main' : 'transparent',
                                color: isSelected ? 'primary.contrastText' : 'text.primary',
                                border: isToday && !isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: isToday || isSelected ? 700 : 500 }}>
                                {cellDate.getDate()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '3px', height: 6 }}>
                              {hasPractice && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DOT.practice }} />}
                              {hasMeet && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DOT.meet }} />}
                              {hasSocial && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DOT.social }} />}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                      {([['Practice', DOT.practice], ['Meet', DOT.meet], ['Event', DOT.social]] as const).map(([label, color]) => (
                        <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Card>
                )}

                {/* Selected-day agenda (the whole view in Day mode; below the grid otherwise) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selHeader}</Typography>
                  {dayAgenda.length > 0 && (
                    <Button size="small" onClick={() => setOpenDate(selectedDate)}>Open day</Button>
                  )}
                </Box>

                {selDailyWorkout ? (
                  <Card sx={{ p: 2, mb: 1, borderLeft: `3px solid ${theme.palette.success.main}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <EventNoteIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                      <Typography variant="subtitle2" color="success.main">Today's workout</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{selDailyWorkout}</Typography>
                  </Card>
                ) : null}

                {dayAgenda.length === 0 ? (
                  <Card sx={{ p: 2 }}>
                    <Typography color="text.secondary">Nothing scheduled this day.</Typography>
                  </Card>
                ) : (
                  <Stack spacing={1}>
                    {dayAgenda.map((it, i) => (
                      <Card
                        key={i}
                        onClick={() => setOpenDate(selectedDate)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          ...(it.kind === 'meet' ? { borderLeft: `3px solid ${theme.palette.warning.main}` } : {}),
                          ...(it.kind === 'social' ? { borderLeft: `3px solid ${theme.palette.info.main}` } : {}),
                          '&:hover': { borderColor: theme.palette.primary.main },
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0 }}>
                            {to12Hour(it.time) || '—'}
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.title}</Typography>
                            {it.sub ? <Typography variant="caption" color="text.secondary">{it.sub}</Typography> : null}
                          </Box>
                          <ChevronRightIcon sx={{ color: 'text.disabled' }} />
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}

        {/* Tab 1: Agenda list */}
        {tabValue === 1 && (
          <Box>
            <ToggleButtonGroup
              size="small"
              value={agendaFilter}
              exclusive
              onChange={(_e, v) => v && setAgendaFilter(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="all">
                All ({practiceCount + meetCount})
              </ToggleButton>
              <ToggleButton value="practice">
                Practice ({practiceCount})
              </ToggleButton>
              <ToggleButton value="meets">
                Meets ({meetCount})
              </ToggleButton>
            </ToggleButtonGroup>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredGrouped.length === 0 ? (
              <EmptyState
                icon={<CalendarTodayIcon />}
                title={
                  agendaFilter === 'meets'
                    ? 'No upcoming meets'
                    : agendaFilter === 'practice'
                    ? 'No upcoming practices'
                    : 'No upcoming sessions'
                }
                body={
                  agendaFilter === 'all'
                    ? 'Browse Swim Classes to enroll, or check back after the club publishes a schedule.'
                    : 'Try a different filter, or check back after the club publishes more.'
                }
              />
            ) : (
              <Stack spacing={3}>
                {filteredGrouped.map((wk) => (
                  <Box key={wk.weekKey}>
                    <Typography variant="overline" color="text.secondary">{weekLabel(wk.weekKey)}</Typography>
                    <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                      {wk.days.map(({ day, list, comps, events: dayEvents, showComp, showPractice, showEvent }) => (
                        <Box key={day}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{dayLabel(day)}</Typography>
                          {showEvent && dayEvents.map((ev) => (
                            <Alert
                              key={ev.id}
                              icon={<CelebrationIcon fontSize="small" />}
                              severity="info"
                              variant="outlined"
                              onClick={() => setOpenDate(day)}
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                color: 'secondary.main',
                                borderColor: 'secondary.main',
                                transition: 'background-color 150ms ease',
                                '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.06)' },
                                '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: 'secondary.main',
                                  outlineOffset: 2,
                                },
                                '& .MuiAlert-icon': { color: 'secondary.main' },
                              }}
                              tabIndex={0}
                              role="button"
                              aria-label={`Open event details for ${ev.name}`}
                              onKeyDown={(keyEv) => {
                                if (keyEv.key === 'Enter' || keyEv.key === ' ') {
                                  keyEv.preventDefault();
                                  setOpenDate(day);
                                }
                              }}
                            >
                              <strong>{ev.name}</strong>
                              {ev.location && ` · ${ev.location}`}
                              {ev.start_time && ` · ${to12Hour(ev.start_time)}`}
                              {ev.notes && (
                                <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                                  <Typography variant="caption">{ev.notes}</Typography>
                                </Box>
                              )}
                            </Alert>
                          ))}
                          {showComp && comps.map((comp) => (
                            <Alert
                              key={comp.id}
                              icon={<EmojiEventsIcon fontSize="small" />}
                              severity="warning"
                              variant="outlined"
                              onClick={() => setOpenDate(day)}
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                transition: 'background-color 150ms ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(237, 108, 2, 0.06)',
                                },
                                '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: 'warning.main',
                                  outlineOffset: 2,
                                },
                              }}
                              tabIndex={0}
                              role="button"
                              aria-label={`Open meet details for ${comp.name}`}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') {
                                  ev.preventDefault();
                                  setOpenDate(day);
                                }
                              }}
                            >
                              <strong>{comp.name}</strong>
                              {comp.location && ` · ${comp.location}`}
                              {comp.start_time && ` · ${to12Hour(comp.start_time)}`}
                              {comp.host_team && ` · hosted by ${comp.host_team}`}
                              {comp.notes && (
                                <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                                  <Typography variant="caption">{comp.notes}</Typography>
                                </Box>
                              )}
                            </Alert>
                          ))}
                          {showPractice && (
                          <Stack spacing={1}>
                            {list.map((e) => {
                              const key = `${e.enrollment_id}|${e.session_id}`;
                              const isCancelled = e.is_cancelled;
                              const isSkip = e.rsvp_status === 'skip';
                              const atMeet = e.at_meet;
                              return (
                                <Card
                                  key={key}
                                  variant="outlined"
                                  sx={{
                                    opacity: isCancelled || isSkip || atMeet ? 0.6 : 1,
                                    borderLeft: 4,
                                    borderColor: isCancelled
                                      ? 'error.main'
                                      : atMeet
                                      ? 'warning.main'
                                      : isSkip
                                      ? 'grey.500'
                                      : 'success.main',
                                  }}
                                >
                                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack
                                      direction={{ xs: 'column', sm: 'row' }}
                                      justifyContent="space-between"
                                      alignItems={{ xs: 'stretch', sm: 'center' }}
                                      spacing={{ xs: 1, sm: 2 }}
                                    >
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="body1"
                                          sx={{ textDecoration: isCancelled ? 'line-through' : 'none' }}
                                        >
                                          <strong>{to12Hour(e.start_time)}</strong>{' '}
                                          <Typography component="span" variant="body2" color="text.secondary">
                                            – {to12Hour(e.end_time)}
                                          </Typography>
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ textDecoration: isCancelled ? 'line-through' : 'none' }}
                                        >
                                          {e.swimmer_name} • {e.program_name}
                                        </Typography>
                                        {isCancelled && e.cancellation_reason && (
                                          <Typography variant="caption" color="error.main">
                                            Cancelled: {e.cancellation_reason}
                                          </Typography>
                                        )}
                                        {!isCancelled && e.workout && (
                                          <>
                                            <Divider sx={{ my: 0.75 }} />
                                            <Typography variant="caption" color="text.secondary">
                                              <strong>Workout:</strong> {e.workout}
                                            </Typography>
                                          </>
                                        )}
                                      </Box>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        {isCancelled ? (
                                          <Chip size="small" color="error" label="Cancelled" />
                                        ) : atMeet ? (
                                          <Chip
                                            size="small"
                                            icon={<EmojiEventsIcon fontSize="small" />}
                                            label="At meet"
                                            color="warning"
                                          />
                                        ) : (
                                          <>
                                            <Chip
                                              size="small"
                                              icon={isSkip ? <DoNotDisturbIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                              label={isSkip ? 'Skipping' : 'Planned'}
                                              color={isSkip ? 'default' : 'success'}
                                              variant={isSkip ? 'outlined' : 'filled'}
                                            />
                                            <Button
                                              size="small"
                                              variant={isSkip ? 'contained' : 'outlined'}
                                              onClick={() => void toggleRSVP(e)}
                                              disabled={busyKey === key}
                                            >
                                              {busyKey === key ? '…' : isSkip ? "I'll come" : "I'll skip"}
                                            </Button>
                                          </>
                                        )}
                                      </Stack>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </Stack>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {openDate && (
          <ParentDayDetailDialog
            date={openDate}
            entries={entriesByDate.get(openDate) ?? []}
            competitions={competitionsByDate.get(openDate) ?? []}
            events={eventsByDate.get(openDate) ?? []}
            onClose={() => setOpenDate(null)}
            onSaved={() => void load()}
          />
        )}
      </Box>
    </Container>
  );
};

export default MyScheduleParentPage;
